import type { ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material/styles'
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

export const tableWrapSx: SxProps<Theme> = {
  overflowX: 'auto',
  overflowY: 'visible',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#ffffff',
  boxShadow: 1,
}

export const tableSx: SxProps<Theme> = {
  width: '100%',
  borderCollapse: 'collapse',
  textAlign: 'left',
  fontSize: 14,
}

export const tableWrapClass = tableWrapSx
export const tableClass = tableSx

export function LicenseTableShell({
  children,
  minWidth = '720px',
}: {
  children: ReactNode
  minWidth?: string
}) {
  return (
    <Box sx={tableWrapSx}>
      <Table sx={{ ...(tableSx as object), minWidth }}>{children}</Table>
    </Box>
  )
}

export function LicenseTableHead({ children }: { children: ReactNode }) {
  return (
    <TableHead
      sx={{
        overflow: 'visible',
        backgroundColor: MISC_PRIMARY,
        '& .MuiTableCell-head': {
          fontSize: 12,
          fontWeight: 600,
          color: '#ffffff',
        },
      }}
    >
      <TableRow>{children}</TableRow>
    </TableHead>
  )
}

export function LicenseTableTh({
  children,
  sx,
  title,
}: {
  children: ReactNode
  sx?: SxProps<Theme>
  title?: string
}) {
  return (
    <TableCell
      component="th"
      scope="col"
      title={title}
      sx={[
        {
          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
          px: 1.5,
          py: 1.5,
          verticalAlign: 'middle',
          '&:last-of-type': { borderRight: 0 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </TableCell>
  )
}

export function LicenseTableBody({ children }: { children: ReactNode }) {
  return <TableBody sx={{ color: '#0f172a' }}>{children}</TableBody>
}

export function LicenseTableRow({
  children,
  striped,
}: {
  children: ReactNode
  striped?: boolean
}) {
  return (
    <TableRow
      sx={{
        borderTop: '1px solid #ffffff',
        backgroundColor: striped ? '#f1f5f9' : '#f8fafc',
      }}
    >
      {children}
    </TableRow>
  )
}

export function LicenseTableTd({
  children,
  sx,
  colSpan,
}: {
  children: ReactNode
  sx?: SxProps<Theme>
  colSpan?: number
}) {
  return (
    <TableCell
      colSpan={colSpan}
      sx={[
        {
          borderRight: '1px solid #ffffff',
          px: 1.5,
          py: 1.25,
          verticalAlign: 'middle',
          '&:last-of-type': { borderRight: 0 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </TableCell>
  )
}

export const licenseCellInputSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: 'transparent',
    fontSize: 14,
    color: '#0f172a',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': { borderColor: '#cbd5e1' },
    },
  },
  '& .MuiOutlinedInput-input': {
    px: 0.5,
    py: 0.25,
    '&::placeholder': { color: '#94a3b8', opacity: 1 },
  },
}

export const licenseCellInputClass = licenseCellInputSx
